import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { Link } from "react-router-dom";
import axios from "axios";
import Swal from "sweetalert2";
import { aofUpload } from "../../../api/bseApi";

const ALLOWED_TYPES = { "image/jpeg": "JPG", "image/jpg": "JPG", "image/png": "PNG", "application/pdf": "PDF" };
const MAX_SIZE_MB = 5;
const MAX_SIZE_BYTES = MAX_SIZE_MB * 1024 * 1024;

/**
 * Convert File to Base64
 */
function fileToBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const result = reader.result;
      const base64 = result.includes(",") ? result.split(",")[1] : result;
      resolve(base64);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

/**
 * AofUploadScreen - BSE StAR MF AOF Image Upload (MFAPI Flag 09)
 * Opens after successful UCC creation or from Client Profile / Document section
 * Receives: ClientCode, PAN
 */
export default function AofUploadScreen() {
  const navigate = useNavigate();
  const location = useLocation();
  const { userId } = useParams();
  const state = location.state;
  const [context, setContext] = useState(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const [fetching, setFetching] = useState(true);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    const clientCode = state?.clientCode;
    const pan = state?.pan || state?.PAN;
    if (clientCode && pan) {
      setContext({ clientCode, pan });
      setFetching(false);
      return;
    }
    if (userId) {
      const fetchUser = async () => {
        try {
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_BASE_URL || "http://localhost:8000/api"}/user/${userId}`
          );
          const user = res.data?.status === 1 ? res.data?.data : null;
          if (!user) {
            Swal.fire("Error", "User not found.", "error").then(() => navigate("/user"));
            return;
          }
          const ucc = user.ucc ?? user.UCC ?? "";
          const panVal = user.pan_number ?? user.pan ?? user.PAN ?? "";
          if (!ucc || String(ucc).trim() === "" || String(ucc) === "Reject") {
            Swal.fire("Error", "ClientCode (UCC) not available.", "error").then(() => navigate("/user"));
            return;
          }
          if (!panVal || String(panVal).trim() === "") {
            Swal.fire("Error", "PAN not available for this user.", "error").then(() => navigate("/user"));
            return;
          }
          setContext({ clientCode: String(ucc).trim(), pan: String(panVal).trim() });
        } catch (err) {
          Swal.fire("Error", err.response?.data?.error || "Failed to load user.", "error").then(() => navigate("/user"));
        } finally {
          setFetching(false);
        }
      };
      fetchUser();
      return;
    }
    Swal.fire(
      "Error",
      "Missing ClientCode or PAN. Open after UCC creation or from Client Profile.",
      "error"
    ).then(() => navigate("/user"));
    setFetching(false);
  }, [location.state, state?.clientCode, state?.pan, state?.PAN, userId, navigate]);

  const handleFileChange = (e) => {
    const selected = e.target.files?.[0];
    setFile(null);
    setPreview(null);
    setErrors((prev) => ({ ...prev, file: null }));

    if (!selected) return;

    const mime = selected.type?.toLowerCase();
    if (!ALLOWED_TYPES[mime]) {
      setErrors((prev) => ({ ...prev, file: "Allowed formats: PDF, JPG, PNG" }));
      return;
    }
    if (selected.size > MAX_SIZE_BYTES) {
      setErrors((prev) => ({ ...prev, file: `File size must be within ${MAX_SIZE_MB} MB` }));
      return;
    }

    setFile(selected);
    if (mime.startsWith("image/")) {
      const reader = new FileReader();
      reader.onload = () => setPreview(reader.result);
      reader.readAsDataURL(selected);
    } else {
      setPreview(null);
    }
  };

  const validate = () => {
    const err = {};
    if (!context?.clientCode?.trim()) err.clientCode = "ClientCode is required";
    if (!context?.pan?.trim()) err.pan = "PAN is required";
    if (!file) err.file = "File must be selected";
    else {
      const mime = file.type?.toLowerCase();
      if (!ALLOWED_TYPES[mime]) err.file = "Allowed formats: PDF, JPG, PNG";
      if (file.size > MAX_SIZE_BYTES) err.file = `File size must be within ${MAX_SIZE_MB} MB`;
    }
    setErrors(err);
    return Object.keys(err).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!context || !validate()) return;

    setLoading(true);
    try {
      const base64Data = await fileToBase64(file);
      const fileType = ALLOWED_TYPES[file.type?.toLowerCase()] || "PDF";
      const fileName = file.name || `aof_${context.clientCode}.${fileType.toLowerCase()}`;

      const payload = {
        clientCode: context.clientCode.trim(),
        pan: context.pan.trim(),
        fileName,
        fileType,
        base64Data,
      };

      const res = await aofUpload(payload);

      if (res.data?.status === 1) {
        const bseFailed = res.data?.bseFailed === true;
        Swal.fire({
          icon: "success",
          title: "AOF Saved",
          text: bseFailed
            ? (res.data?.message || "AOF details saved. BSE update failed; you can retry later.")
            : (res.data?.message || "AOF uploaded successfully."),
        });
        navigate("/user", { state: { aofSubmitted: true } });
      } else {
        const msg = res.data?.bseMessage || res.data?.message || res.data?.error || "AOF upload failed";
        Swal.fire("Error", msg, "error");
      }
    } catch (err) {
      const d = err.response?.data;
      const msg =
        (typeof d?.message === "string" && d.message) ||
        (typeof d?.bseMessage === "string" && d.bseMessage) ||
        (typeof d?.error === "string" && d.error) ||
        (typeof d === "string" && d) ||
        (err.response?.status === 400 && "Invalid request. Check client code, PAN and document file.") ||
        (err.response?.status >= 500 && "Server error. Try again or contact support.") ||
        err.message ||
        "AOF upload failed";
      Swal.fire("Error", msg, "error");
    } finally {
      setLoading(false);
    }
  };

  if (fetching || !context) {
    return (
      <div className="main-content">
        <div className="page-wrapper page-settings">
          <div className="content p-4">
            <div className="text-center">
              <span className="spinner-border spinner-border-sm me-2" role="status" />
              Loading...
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="main-content">
      <div className="page-wrapper page-settings">
        <div className="content">
          <div
            className="mb-4"
            style={{
              background: "#ffffff",
              borderRadius: "8px",
              boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
              padding: "20px",
            }}
          >
            <nav aria-label="breadcrumb" style={{ marginBottom: "15px" }}>
              <ol className="breadcrumb">
                <li className="breadcrumb-item">
                  <Link to="/dashboard">Dashboard</Link>
                </li>
                <li className="breadcrumb-item">
                  <Link to="/user">Users</Link>
                </li>
                <li className="breadcrumb-item active">AOF Upload</li>
              </ol>
            </nav>
            <h5>AOF Document Upload</h5>
            <p className="text-muted mb-0">
              Client Code: <strong>{context.clientCode}</strong> | PAN: <strong>{context.pan}</strong>
            </p>
          </div>

          <form onSubmit={handleSubmit}>
            <div
              className="mb-4"
              style={{
                background: "#ffffff",
                borderRadius: "8px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                padding: "24px",
                border: "1px solid #e6eef0",
              }}
            >
              <h6 className="mb-4" style={{ color: "#0b6f68", fontWeight: "600" }}>
                AOF Document Upload
              </h6>
              <div className="row g-3">
                <div className="col-md-6">
                  <label className="form-label">Client Code</label>
                  <input
                    type="text"
                    className="form-control"
                    value={context.clientCode}
                    readOnly
                    style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                  />
                </div>
                <div className="col-md-6">
                  <label className="form-label">PAN</label>
                  <input
                    type="text"
                    className="form-control"
                    value={context.pan}
                    readOnly
                    style={{ backgroundColor: "#f8f9fa", cursor: "not-allowed" }}
                  />
                </div>
                <div className="col-12">
                  <label className="form-label">
                    Upload File (PDF, JPG, PNG) <span className="text-danger">*</span>
                  </label>
                  <input
                    type="file"
                    className={`form-control ${errors.file ? "is-invalid" : ""}`}
                    accept=".jpg,.jpeg,.png,.pdf,image/jpeg,image/png,application/pdf"
                    onChange={handleFileChange}
                  />
                  <small className="text-muted">Max size: {MAX_SIZE_MB} MB</small>
                  {errors.file && <div className="invalid-feedback d-block">{errors.file}</div>}
                </div>
                {preview && (
                  <div className="col-12">
                    <label className="form-label">Preview</label>
                    <div className="border rounded p-2" style={{ maxWidth: "300px", maxHeight: "200px" }}>
                      <img
                        src={preview}
                        alt="Preview"
                        style={{ maxWidth: "100%", maxHeight: "180px", objectFit: "contain" }}
                      />
                    </div>
                  </div>
                )}
                {file && !preview && (
                  <div className="col-12">
                    <label className="form-label">Selected File</label>
                    <p className="form-control-plaintext text-muted">
                      {file.name} ({(file.size / 1024).toFixed(1)} KB)
                    </p>
                  </div>
                )}
              </div>

              <div
                className="d-flex gap-2 justify-content-end mt-4 pt-3"
                style={{ borderTop: "1px solid #e6eef0" }}
              >
                <Link to="/user" className="btn btn-secondary">
                  Cancel
                </Link>
                <button type="submit" className="btn btn-primary" disabled={loading}>
                  {loading ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" role="status" />
                      Uploading...
                    </>
                  ) : (
                    "Upload AOF"
                  )}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}
